/**
 * Drone Operator Coverage geometry + reachability (L.2.2, §3B.6).
 *
 * UNIFIED with the marketplace fleet (Wave-1 drone unify #343): coverage is sourced from
 * `drone_assets`, so the coverage circle = the asset's `service_radius_km` and
 * reachability is a single radius test — the legacy drive/flight-radius split (the
 * `drone_capabilities` blob) is gone. The unified model carries no per-asset launch /
 * transit speed, so the on-station figure is a COARSE transit estimate at a nominal
 * cruise speed, surfaced as an estimate (the matcher likewise returns eta_minutes=null
 * until the #318 availability/speed fields land). Pure functions so reachability + the
 * estimate are unit-testable (no Cesium/DOM).
 */
import {
  computeDroneEtaMinutes,
  type DronePlatformCategory,
  type DroneSensorType,
} from '@tsr/shared-types';
import { destinationPoint } from './viewshedSample';

/** Nominal drone cruise (km/h) for the coarse on-station transit estimate. */
export const DRONE_CRUISE_KMH = 60;

/** Ambient drone-base hue (desaturated violet) — the §3B.8 asset register. */
export const DRONE_AMBIENT_COLOR = '#a78bda';
/**
 * Resting coverage-fill opacity — OVERLAP-BUDGETED (Phase 6 legibility). Lowered
 * 0.15 → 0.07 so the outline, not the fill, carries "where the edge is": at α0.07
 * three overlapping bubbles composite (1−(1−α)^N) to ~0.20 — a faint wash, never
 * an opaque blob that buries the communities/closures/assets painted over it. The
 * reachable highlight (× DRONE_COVERAGE_HIGHLIGHT_MULT ≈ 0.15) still reads as the old
 * resting fill — it preserves the fill-contrast RATIO over the fainter resting wash,
 * while the reachable RING carries the absolute salience. Pinned by droneCoverage.test.ts.
 */
export const DRONE_COVERAGE_ALPHA = 0.07;

/**
 * Reachable-highlight fill multiplier — the selected-incident-reachable coverage
 * fill is `DRONE_COVERAGE_ALPHA × this`. At α0.07 that is ~0.154 (≈ the OLD resting
 * fill), preserving a 2.2× fill-contrast ratio over the fainter resting wash; the
 * reachable RING (2px/α0.9) carries the absolute salience. Shared by
 * DroneCoverageLayer + droneCoverage.test.ts so the pin tracks the real render path.
 */
export const DRONE_COVERAGE_HIGHLIGHT_MULT = 2.2;

/** Bow-up quadcopter glyph (4 rotors + body), fill="currentColor". */
export const DRONE_GLYPH_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle fill="currentColor" cx="5" cy="5" r="3"/><circle fill="currentColor" cx="19" cy="5" r="3"/><circle fill="currentColor" cx="5" cy="19" r="3"/><circle fill="currentColor" cx="19" cy="19" r="3"/><rect fill="currentColor" x="4" y="11" width="16" height="2" rx="1"/><rect fill="currentColor" x="11" y="4" width="2" height="16" rx="1"/><rect fill="currentColor" x="9" y="9" width="6" height="6" rx="1.5"/></svg>';

/** Great-circle distance in km (haversine). */
export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371.0088;
  const toRad = (d: number): number => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Coverage-circle radius (km) = the asset's service radius. 0 when unknown. */
export function coverageRadiusKm(serviceRadiusKm: number | null | undefined): number {
  return serviceRadiusKm && serviceRadiusKm > 0 ? serviceRadiusKm : 0;
}

/**
 * BVLOS coverage-display realism (#374). Most Canadian beyond-visual-line-of-sight drone
 * ops are practically capped near 50 km by detect-and-avoid range — yet an operator's
 * stated `service_radius_km` (illustrative seeds run 53–400 km; the portal accepts up to
 * 100 000) paints a coverage bubble far larger than a single sortie can realistically hold.
 * This is a DISPLAY cap only: it shrinks the rendered bubble + the panel's shown radius to a
 * realistic ceiling and flags anything beyond as aspirational. It deliberately does NOT touch
 * `coverageRadiusKm` / `canReach` / `deriveReach` — the #320 matcher + reachability still read
 * the operator's true stated radius (the NW-Ontario fly-in matching depends on it); the panel
 * frames that honestly instead.
 */
export const BVLOS_DISPLAY_CAP_KM = 50;

/** The radius to RENDER (km) — the asset's service radius clamped to the BVLOS display cap.
 *  0 when unknown (no bubble). */
export function displayCoverageRadiusKm(serviceRadiusKm: number | null | undefined): number {
  return Math.min(coverageRadiusKm(serviceRadiusKm), BVLOS_DISPLAY_CAP_KM);
}

/** True when the asset's stated service radius exceeds the realistic BVLOS display cap, so the
 *  rendered bubble is a capped subset of the provider's claim — surfaced as "aspirational". */
export function isCoverageOverProvisioned(serviceRadiusKm: number | null | undefined): boolean {
  return coverageRadiusKm(serviceRadiusKm) > BVLOS_DISPLAY_CAP_KM;
}

/**
 * A geodesic ring of `[lon, lat]` points (degrees) at `radiusKm` around a centre — the dashed
 * "display cap" boundary drawn for an over-provisioned coverage bubble. Pure (no Cesium) so the
 * geometry is unit-testable; the layer feeds it to `Cesium.Cartesian3.fromDegreesArray`. Built by
 * sweeping the shared great-circle `destinationPoint` over n+1 bearings (closed loop); segments
 * floored at 8.
 */
export function geodesicRingDegrees(
  centerLat: number,
  centerLon: number,
  radiusKm: number,
  segments = 64,
): number[] {
  const origin = { lat: centerLat, lon: centerLon };
  const distanceM = radiusKm * 1000;
  const out: number[] = [];
  const n = Math.max(8, Math.floor(segments));
  for (let i = 0; i <= n; i++) {
    const p = destinationPoint(origin, (360 * i) / n, distanceM);
    out.push(p.lon, p.lat);
  }
  return out;
}

/** Can a drone from this provider reach (lat, lon)? — within its service radius. */
export function canReach(
  serviceRadiusKm: number | null | undefined,
  providerLat: number,
  providerLon: number,
  incidentLat: number,
  incidentLon: number,
): boolean {
  const r = coverageRadiusKm(serviceRadiusKm);
  return r > 0 && haversineKm(providerLat, providerLon, incidentLat, incidentLon) <= r;
}

/** Per-asset response-time telemetry (Phase 4c, drone_assets cols) — the inputs to the real
 *  derived ETA-to-on-station. Optional on deriveReach so callers without telemetry fall back
 *  to the coarse nominal-cruise estimate. */
export interface DroneReachTelemetry {
  cruiseSpeedKmh: number | null;
  launchDelayMinutes: number | null;
}

export interface DroneReach {
  /** Straight-line distance provider → incident (km, 1 dp). */
  distanceKm: number;
  /** The asset's service radius (km). */
  serviceRadiusKm: number;
  /** `serviceRadius − distance`, floored at 0 (km, 1 dp) — the range to spare. */
  slackKm: number;
  /** COARSE transit estimate at DRONE_CRUISE_KMH (minutes), rounded; null when
   *  unreachable. A nominal-cruise estimate (no launch), the fallback for an asset with no
   *  per-asset telemetry — the UI labels it as such. */
  estTransitMin: number | null;
  /** Phase 4c — the REAL ETA-to-on-station (minutes) = launch + transit, via the shared
   *  `computeDroneEtaMinutes` over the asset's telemetry. null when telemetry is absent OR
   *  the incident is unreachable. The headline §3B.6 figure. */
  etaMinutes: number | null;
  reachable: boolean;
}

/**
 * Reachability + the on-station ETA for an incident, derived from the asset's service radius
 * and (Phase 4c) its response-time telemetry. Beyond the radius the provider can't get on
 * station. `etaMinutes` is the real launch + transit figure when telemetry is supplied; the
 * coarse `estTransitMin` remains as the nominal-cruise fallback.
 */
export function deriveReach(
  serviceRadiusKm: number | null | undefined,
  providerLat: number,
  providerLon: number,
  incidentLat: number,
  incidentLon: number,
  telemetry?: DroneReachTelemetry,
): DroneReach {
  const r = coverageRadiusKm(serviceRadiusKm);
  const distanceKm = haversineKm(providerLat, providerLon, incidentLat, incidentLon);
  const reachable = r > 0 && distanceKm <= r;
  return {
    distanceKm: round1(distanceKm),
    serviceRadiusKm: r,
    slackKm: Math.max(0, round1(r - distanceKm)),
    estTransitMin: reachable ? Math.round((distanceKm / DRONE_CRUISE_KMH) * 60) : null,
    etaMinutes: reachable
      ? computeDroneEtaMinutes({
          distanceKm,
          cruiseSpeedKmh: telemetry?.cruiseSpeedKmh ?? null,
          launchDelayMinutes: telemetry?.launchDelayMinutes ?? null,
        })
      : null,
    reachable,
  };
}

/** Human label for a drone sensor type (shared with the §3B.6 panels). */
export const DRONE_SENSOR_LABEL: Record<DroneSensorType, string> = {
  eo: 'EO (daylight)',
  ir: 'IR / thermal',
  multispectral: 'Multispectral',
  lidar: 'LiDAR',
  sar: 'SAR',
  gas: 'Gas / chem',
  'comms-relay': 'Comms relay',
};

/** Human label for a drone platform category. */
export const DRONE_PLATFORM_LABEL: Record<DronePlatformCategory, string> = {
  multirotor: 'Multirotor',
  'fixed-wing': 'Fixed-wing',
  vtol: 'VTOL',
  male: 'MALE',
};

export function sensorLabel(s: DroneSensorType): string {
  return DRONE_SENSOR_LABEL[s] ?? s;
}

export function platformLabel(p: DronePlatformCategory | null): string {
  return p ? (DRONE_PLATFORM_LABEL[p] ?? p) : 'Platform —';
}

/**
 * Pricing label for a coverage asset / operator (#343). A government / public-service
 * operator is $0-rated; a commercial operator quotes its price on accept-with-terms.
 */
export function dronePriceLabel(governmentOwned: boolean): string {
  return governmentOwned ? '$0 · Government asset' : 'Quote on accept';
}
